import { Box, Grid, GridItem, Text } from "@chakra-ui/react";
import { format, isSameMonth, isToday, isWeekend } from "date-fns";
import { deLocaleOption, formatIso8601 } from "@/data/DateTimeUtil";
import { Holiday } from "@/api/Holiday";
import { Timesheet as TimesheetEntry } from "@/api/Timesheet";
import IdAndNameData from "@/data/IdAndNameData";
import { Absence } from "@/api/Absence";
import TimesheetCell from "./TimesheetCell";
import { filterWeekendDays, getEntriesForDate, TimesheetView } from "./TimesheetCalendarUtils";

type TimesheetEntryHandlers = {
  onEditEntry: (entry: TimesheetEntry) => void;
  onDeleteEntry: (data: IdAndNameData<number>) => void;
  onApproveEntry: (entry: TimesheetEntry) => void;
  onNewEntry: (day: Date, entries: TimesheetEntry[]) => void;
  canEditEntry: (entry: TimesheetEntry) => boolean;
  canCancelEntry: (entry: TimesheetEntry) => boolean;
  canApproveEntry: (entry: TimesheetEntry) => boolean;
  canCreateEntry: boolean;
};

type TimesheetDayTileProps = TimesheetEntryHandlers & {
  day: Date;
  monthStart: Date;
  view: TimesheetView;
  dailyEntries: TimesheetEntry[];
  holiday?: Holiday;
  absences: Absence[];
  projects: Map<number, string>;
  variant: "desktop" | "mobile";
};

type TimesheetCalendarGridProps = TimesheetEntryHandlers & {
  view: TimesheetView;
  showWeekends: boolean;
  weekdays: Date[];
  weeks: Date[][];
  monthStart: Date;
  entriesByDate: Map<string, TimesheetEntry[]>;
  holidayMap: Map<string, Holiday>;
  absenceMap: Map<string, Absence[]>;
  projects: Map<number, string>;
};

type TimesheetPeriodGridProps = Omit<TimesheetCalendarGridProps, "showWeekends">;

const DAY_COLUMN_MIN_WIDTH = "10rem";
const DESKTOP_CALENDAR_MIN_WIDTH = "70rem";
const WEEK_ROW_HEIGHT = "10rem";
const EXPANDED_PERIOD_ROW_HEIGHT = "32rem";
const GRID_GAP = 1;
const ABSENCE_BACKGROUND =
  "repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(0, 100, 0, 0.2) 4px, rgba(0, 200, 0, 0.2) 10px)";
const HOLIDAY_BACKGROUND =
  "repeating-linear-gradient(135deg, transparent, transparent 9px, rgba(0,200,0,0.2) 8px, rgba(0,200,0,0.2) 10px)";
const EMPTY_ABSENCES: Absence[] = [];

const getDayBackgroundImage = (holiday: Holiday | undefined, absences: Absence[]): string => {
  if (absences.length > 0) {
    return ABSENCE_BACKGROUND;
  }

  if (holiday) {
    return HOLIDAY_BACKGROUND;
  }

  return "none";
};

const dayTemplateColumns = (dayCount: number): string =>
  `repeat(${dayCount}, minmax(${DAY_COLUMN_MIN_WIDTH}, 1fr))`;

const rowHeightForView = (view: TimesheetView): string =>
  view === "month" ? WEEK_ROW_HEIGHT : EXPANDED_PERIOD_ROW_HEIGHT;

const TimesheetWeekdayHeader = ({ weekdays }: { weekdays: Date[] }) => (
  <Grid
    templateColumns={dayTemplateColumns(weekdays.length)}
    gap={GRID_GAP}
    position="sticky"
    top={0}
    zIndex={1}
    bg="bg.panel"
    py="2"
  >
    {weekdays.map((day) => (
      <GridItem key={day.toString()} minW={0}>
        <Text
          textAlign="center"
          fontSize={{ md: "sm", xl: "md" }}
          fontWeight="semibold"
          color="fg.muted"
          textTransform="uppercase"
        >
          {format(day, "EEEE", deLocaleOption)}
        </Text>
      </GridItem>
    ))}
  </Grid>
);

const TimesheetDayTile = ({
  day,
  monthStart,
  view,
  dailyEntries,
  holiday,
  absences,
  projects,
  variant,
  onEditEntry,
  onDeleteEntry,
  onApproveEntry,
  onNewEntry,
  canEditEntry,
  canCancelEntry,
  canApproveEntry,
  canCreateEntry,
}: TimesheetDayTileProps) => {
  const isCurrentMonth = view !== "month" || isSameMonth(day, monthStart);
  const today = isToday(day);
  const isMobile = variant === "mobile";
  const rowHeight = rowHeightForView(view);
  const visibleEntries = view === "month" ? 2 : 4;
  const cellProps = {
    day,
    dailyEntries,
    holiday,
    absences,
    projects,
    onEditEntry,
    onDeleteEntry,
    onApproveEntry,
    onNewEntry,
    canEditEntry,
    canCancelEntry,
    canApproveEntry,
    canCreateEntry,
    visibleEntries,
  };

  return (
    <GridItem
      as="section"
      mt={0}
      p={isMobile ? "2" : 0}
      position="relative"
      minH={isMobile ? "auto" : rowHeight}
      minW='0'
      bg={today ? "blue.subtle" : isCurrentMonth ? (isWeekend(day) ? "bg.subtle" : "bg.panel") : "bg.muted"}
      color={isCurrentMonth ? "fg" : "fg.muted"}
      backgroundImage={getDayBackgroundImage(holiday, absences)}
      borderWidth="1px"
      borderColor={today ? "blue.500" : "border"}
      borderRadius="md"
      boxShadow={today ? "md" : "xs"}
      textAlign="center"
      cursor="pointer"
      overflow="visible"
      transition="box-shadow 0.15s ease, border-color 0.15s ease"
      _hover={isMobile ? undefined : {
        borderColor: today ? "blue.500" : "blue.300",
        boxShadow: "xs",
        "& .entries-container": { display: "block", zIndex: 10 },
      }}
    >
      <Box m={isMobile ? 0 : "1px"}>
        <TimesheetCell hover={isMobile} actionsVisible={isMobile} {...cellProps} />
      </Box>

      {!isMobile && (
        <Box
          className="entries-container"
          position="absolute"
          top="0"
          left="0"
          right="0"
          display="none"
          bg="bg.panel"
          borderWidth="1px"
          borderColor="blue.300"
          boxShadow="lg"
          borderRadius="md"
          p="0"
          pb="1.5rem"
          maxH={view === "month" ? "350px" : rowHeight}
          minH={rowHeight}
          overflowY="auto"
          zIndex={1000}
        >
          <TimesheetCell hover={true} {...cellProps} />
        </Box>
      )}
    </GridItem>
  );
};

const TimesheetDesktopCalendar = ({
  view,
  weekdays,
  weeks,
  monthStart,
  entriesByDate,
  holidayMap,
  absenceMap,
  projects,
  onEditEntry,
  onDeleteEntry,
  onApproveEntry,
  onNewEntry,
  canEditEntry,
  canCancelEntry,
  canApproveEntry,
  canCreateEntry,
}: TimesheetPeriodGridProps) => (
  <Box hideBelow="md" overflowX="auto" overflowY="visible" pb="3">
    <Box minW={view === "day" ? "0" : view === "week" ? "50rem" : DESKTOP_CALENDAR_MIN_WIDTH}>
      <TimesheetWeekdayHeader weekdays={weekdays} />
      <Box display="grid" gap={GRID_GAP}>
        {weeks.map((week) => (
          <Grid templateColumns={dayTemplateColumns(week.length)} minH={rowHeightForView(view)} gap={GRID_GAP} key={formatIso8601(week[0])}>
            {week.map((day) => {
              const dayKey = formatIso8601(day);

              return (
                <TimesheetDayTile
                  key={dayKey}
                  day={day}
                  monthStart={monthStart}
                  view={view}
                  dailyEntries={getEntriesForDate(entriesByDate, day)}
                  holiday={holidayMap.get(dayKey)}
                  absences={absenceMap.get(dayKey) ?? EMPTY_ABSENCES}
                  projects={projects}
                  variant="desktop"
                  onEditEntry={onEditEntry}
                  onDeleteEntry={onDeleteEntry}
                  onApproveEntry={onApproveEntry}
                  onNewEntry={onNewEntry}
                  canEditEntry={canEditEntry}
                  canCancelEntry={canCancelEntry}
                  canApproveEntry={canApproveEntry}
                  canCreateEntry={canCreateEntry}
                />
              );
            })}
          </Grid>
        ))}
      </Box>
    </Box>
  </Box>
);

const TimesheetMobileAgenda = ({
  view,
  weeks,
  monthStart,
  entriesByDate,
  holidayMap,
  absenceMap,
  projects,
  onEditEntry,
  onDeleteEntry,
  onApproveEntry,
  onNewEntry,
  canEditEntry,
  canCancelEntry,
  canApproveEntry,
  canCreateEntry,
}: TimesheetPeriodGridProps) => {
  const periodDays = view === "month"
    ? weeks.flat().filter((day) => isSameMonth(day, monthStart))
    : weeks.flat();

  return (
    <Box hideFrom="md" display="grid" gridTemplateColumns='minmax(0, 1fr)' gap="3">
      {periodDays.map((day) => {
        const dayKey = formatIso8601(day);

        return (
          <TimesheetDayTile
            key={dayKey}
            day={day}
            monthStart={monthStart}
            view={view}
            dailyEntries={getEntriesForDate(entriesByDate, day)}
            holiday={holidayMap.get(dayKey)}
            absences={absenceMap.get(dayKey) ?? EMPTY_ABSENCES}
            projects={projects}
            variant="mobile"
            onEditEntry={onEditEntry}
            onDeleteEntry={onDeleteEntry}
            onApproveEntry={onApproveEntry}
            onNewEntry={onNewEntry}
            canEditEntry={canEditEntry}
            canCancelEntry={canCancelEntry}
            canApproveEntry={canApproveEntry}
            canCreateEntry={canCreateEntry}
          />
        );
      })}
    </Box>
  );
};

export const TimesheetCalendarGrid = ({
  view,
  showWeekends,
  weekdays,
  weeks,
  monthStart,
  entriesByDate,
  holidayMap,
  absenceMap,
  projects,
  onEditEntry,
  onDeleteEntry,
  onApproveEntry,
  onNewEntry,
  canEditEntry,
  canCancelEntry,
  canApproveEntry,
  canCreateEntry,
}: TimesheetCalendarGridProps) => {
  const visibleWeekdays = filterWeekendDays(weekdays, view, showWeekends);
  const visibleWeeks = weeks.map((week) => filterWeekendDays(week, view, showWeekends));

  return (
    <Box w="full">
      <TimesheetDesktopCalendar
        view={view}
        weekdays={visibleWeekdays}
        weeks={visibleWeeks}
        monthStart={monthStart}
        entriesByDate={entriesByDate}
        holidayMap={holidayMap}
        absenceMap={absenceMap}
        projects={projects}
        onEditEntry={onEditEntry}
        onDeleteEntry={onDeleteEntry}
        onApproveEntry={onApproveEntry}
        onNewEntry={onNewEntry}
        canEditEntry={canEditEntry}
        canCancelEntry={canCancelEntry}
        canApproveEntry={canApproveEntry}
        canCreateEntry={canCreateEntry}
      />
      <TimesheetMobileAgenda
        view={view}
        weekdays={visibleWeekdays}
        weeks={visibleWeeks}
        monthStart={monthStart}
        entriesByDate={entriesByDate}
        holidayMap={holidayMap}
        absenceMap={absenceMap}
        projects={projects}
        onEditEntry={onEditEntry}
        onDeleteEntry={onDeleteEntry}
        onApproveEntry={onApproveEntry}
        onNewEntry={onNewEntry}
        canEditEntry={canEditEntry}
        canCancelEntry={canCancelEntry}
        canApproveEntry={canApproveEntry}
        canCreateEntry={canCreateEntry}
      />
    </Box>
  );
};
